import type { SqlDriver, SqlParam, SqlRow, SqlStatement } from '../driver.ts'
import type { WorkerRequest, WorkerResponse } from './protocol.ts'

/** What the driver needs from a Worker (a real one, or a test double). */
export interface WorkerLike {
  postMessage(message: WorkerRequest): void
  addEventListener(type: 'message', listener: (event: { data: WorkerResponse }) => void): void
  terminate(): void
}

type Request = WorkerRequest extends infer R
  ? R extends { id: number }
    ? Omit<R, 'id'>
    : never
  : never

function rpc(worker: WorkerLike) {
  let nextId = 1
  let closed = false
  const waiting = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>()
  worker.addEventListener('message', ({ data }) => {
    const entry = waiting.get(data.id)
    if (!entry) return
    waiting.delete(data.id)
    if (data.ok) entry.resolve(data.result)
    else entry.reject(new Error(data.error))
  })
  const call = (request: Request) =>
    new Promise<unknown>((resolve, reject) => {
      if (closed) return reject(new Error('the local database is closed'))
      const id = nextId++
      waiting.set(id, { resolve, reject })
      worker.postMessage({ ...request, id } as WorkerRequest)
    })
  /** Terminates the worker; calls in flight or made later fail instead of hanging. */
  const terminate = () => {
    closed = true
    worker.terminate()
    for (const entry of waiting.values()) entry.reject(new Error('the local database is closed'))
    waiting.clear()
  }
  return Object.assign(call, { terminate })
}

/** Per-user, per-tenant database file (logout deletes it). */
export function databaseFileName(userId: string, tenantId: string): string {
  return `ims-${userId}-${tenantId}.sqlite3`
}

/** Opens `filename` in the SQLite worker and returns a SqlDriver for it. */
export async function createWorkerDriver(worker: WorkerLike, filename: string): Promise<SqlDriver> {
  const call = rpc(worker)
  await call({ type: 'open', filename })
  return {
    all: async <T = SqlRow>(sql: string, params: readonly SqlParam[] = []) =>
      (await call({ type: 'all', sql, params })) as T[],
    batch: async (statements: readonly SqlStatement[]) => {
      await call({ type: 'batch', statements })
    },
    close: async () => {
      try {
        await call({ type: 'close' })
      } finally {
        call.terminate()
      }
    },
  }
}

/** Deletes a database file (logout). The worker must not have it open elsewhere. */
export async function deleteDatabase(worker: WorkerLike, filename: string): Promise<void> {
  const call = rpc(worker)
  try {
    await call({ type: 'delete', filename })
  } finally {
    call.terminate()
  }
}
